import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Compass, ArrowRight } from "lucide-react";
import { relatedLinksFor, CLUSTERS, PAGES } from "@/lib/seoLinks";

/**
 * RelatedLinks — a contextual "Related pages" block rendered near the
 * bottom of every public page. Pulls the current route's cluster from
 * seoLinks.js, lists the cluster's hub + siblings using *descriptive*
 * page titles as anchor text, and renders each as a crawlable `<a>` so
 * Google treats this as a bi-directional topic-hub link graph instead
 * of relying on the sitemap alone.
 *
 * Props:
 *   - path (optional): override route lookup, defaults to current pathname.
 *   - heading (optional): override the auto-selected "Related …" heading.
 *   - limit (optional): max links to render (default 6).
 *   - variant (optional): "default" (card) | "footer" (slim, used in Footer).
 */
export default function RelatedLinks({ path, heading, limit = 6, variant = "default" }) {
  const loc = useLocation();
  const resolved = path || loc.pathname;
  const links = relatedLinksFor(resolved, limit);
  if (!links.length) return null;

  const page = PAGES[(resolved || "").replace(/\/$/, "") || "/"];
  const clusterLabel = page?.cluster ? CLUSTERS[page.cluster]?.label : null;
  const autoHeading = heading || `Keep exploring${clusterLabel ? ` · ${clusterLabel}` : ""}`;

  if (variant === "footer") {
    return (
      <nav
        aria-label="Related pages"
        className="text-xs text-muted-foreground"
        data-testid="related-links-footer"
      >
        <div className="mono text-[9px] uppercase tracking-widest mb-2">{autoHeading}</div>
        <ul className="flex flex-wrap gap-x-4 gap-y-1.5">
          {links.map((l) => (
            <li key={l.href}>
              <Link
                to={l.href}
                className="hover:text-primary transition-colors"
                data-testid={`related-link-${l.href}`}
              >
                {l.label}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    );
  }

  return (
    <section
      aria-label="Related pages"
      className="mt-16 mb-8 rounded-xl border border-border/60 bg-muted/30 p-6"
      data-testid="related-links"
    >
      <div className="flex items-center gap-2 mono text-[10px] uppercase tracking-[0.25em] text-primary mb-3">
        <Compass className="w-3.5 h-3.5" />
        {autoHeading}
      </div>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
        {links.map((l) => (
          <li key={l.href}>
            <Link
              to={l.href}
              className="group block"
              data-testid={`related-link-${l.href}`}
            >
              <div className="serif text-sm text-foreground group-hover:text-primary transition-colors inline-flex items-center gap-1">
                {l.label}
                <ArrowRight className="w-3 h-3 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                {l.isHub && (
                  <span className="ml-1 inline-flex items-center gap-1 mono text-[8px] uppercase tracking-widest text-secondary">
                    hub
                  </span>
                )}
              </div>
              {l.description && (
                <div className="text-xs text-muted-foreground mt-0.5 leading-snug">
                  {l.description}
                </div>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
